import css from './dashboard.module.scss';

import dayjs from 'dayjs';
import { useState, useEffect, useMemo, memo } from 'react';

import { apiGetDashboard } from 'core/api';
import { waitForPendingQueries } from 'core/api/utils';
import { useStableCallback } from 'core/hooks/useStableCallback';
import { ErrorPane } from 'core/ui/Error';
import { FrameMessage, FramePreloader } from 'core/ui/Frame';

import AppHeader from './AppHeader';
import { DeckPanel } from './DeckPanel';
import NumbersTile from './NumbersTile';
import { TestPanel } from './TestPanel';

const Dashboard: React.FC = () => {
  const [{ dashboard, error }, setData] = useState<{
    dashboard: TDashboard | null;
    error: TAnyError | null;
  }>({ dashboard: null, error: null });
  const { tests = null, decks = null } = dashboard ?? {};
  const isLoading = !error && dashboard === null;

  const reload = useStableCallback(async () => {
    try {
      // wait until possible card-updating query if finished
      await waitForPendingQueries();
      setData({ dashboard: await apiGetDashboard(), error: null });
    } catch (err: unknown) {
      setData({ dashboard: null, error: err as TAnyError });
    }
  });

  const closest = useClosestPending(dashboard);

  useEffect(() => {
    void reload();

    const closestTimeout =
      closest && dayjs.unix(closest).diff(dayjs(), 'millisecond');
    if (closestTimeout) {
      const timeout = setTimeout(() => void reload(), closestTimeout + 2_000);
      return () => clearTimeout(timeout);
    }
  }, [closest]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void reload();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useDynamicTitle(dashboard?.pending);
  useDynamicFavicon((dashboard?.pending ?? 0) > 0);

  const sortedContainers = useMemo(
    (): TContainer[] =>
      [
        ...(decks ?? []).map((deck): TContainer => ({ type: 'deck', deck })),
        ...(tests ?? []).map((test): TContainer => ({ type: 'test', test })),
      ].sort(sortContainers),
    [decks, tests]
  );

  return (
    <>
      <AppHeader dashboard={dashboard} />

      <div className={css.list}>
        {error && <ErrorPane error={error} showStackTrace />}

        {isLoading && <FramePreloader />}

        {decks && !decks.length && <FrameMessage message="no decks" />}

        {sortedContainers?.map(item =>
          item.type === 'deck' ? (
            <DeckPanel deck={item.deck} reload={reload} key={item.deck.uid} />
          ) : (
            <TestPanel test={item.test} key={item.test.uid} reload={reload} />
          )
        )}

        {!isLoading &&
          NUMBER_LANGS.map(lang => <NumbersTile lang={lang} key={lang} />)}
      </div>
    </>
  );
};

export default memo(Dashboard);

function useClosestPending(dashboard: TDashboard | null): number | null {
  return useMemo(() => {
    if (!dashboard || dashboard.total === 0 || dashboard.pending > 0) {
      return null;
    }

    return [
      ...dashboard.decks.flatMap(deck => [
        deck.status.wordCardsReverse?.closest,
        deck.status.wordCards?.closest,
      ]),
      ...dashboard.tests.map(test => test.status.closest),
    ]
      .filter((ts): ts is string => !!ts)
      .reduce(
        (closest, ts) => Math.min(closest, dayjs(ts).unix()),
        Number.MAX_SAFE_INTEGER
      );
  }, [dashboard]);
}

function sortContainers(a: TContainer, b: TContainer): number {
  const aPending =
    a.type === 'deck' ? a.deck.pendingWordsCount : a.test.pendingQuestionsCount;
  const bPending =
    b.type === 'deck' ? b.deck.pendingWordsCount : b.test.pendingQuestionsCount;
  if (aPending !== bPending) {
    return bPending - aPending;
  }

  return getClosest(a) - getClosest(b);
}

function getClosest(container: TContainer): number {
  if (container.type === 'test') {
    return container.test.status.closest
      ? dayjs(container.test.status.closest).unix()
      : Number.MAX_SAFE_INTEGER;
  }

  return Object.values(container.deck.status)
    .filter(st => !!st)
    .map(st =>
      st.closest ? dayjs(st.closest).unix() : Number.MAX_SAFE_INTEGER
    )
    .reduce((min, val) => Math.min(min, val), Number.MAX_SAFE_INTEGER);
}

type TContainer =
  | { type: 'deck'; deck: TDeckRich }
  | { type: 'test'; test: TTestRich };

const NUMBER_LANGS: TLang[] = [TLang.de];

const BACKUP_ATTR = 'data-original';

function useDynamicTitle(pending: number | undefined) {
  useEffect(() => {
    if (typeof pending !== 'number') return;

    const title = orDie(document.querySelector('title'));
    let originalTitle = title.getAttribute(BACKUP_ATTR);
    if (!originalTitle) {
      originalTitle = title.innerText;
      title.setAttribute(BACKUP_ATTR, originalTitle);
    }

    title.innerText = `${pending} - ${originalTitle}`;

    return () => {
      title.innerText = originalTitle;
    };
  }, [pending]);
}

function useDynamicFavicon(hasPending: boolean) {
  useEffect(() => {
    const link = orDie(document.querySelector('link[rel="icon"]'));
    let originalHref = link.getAttribute(BACKUP_ATTR);
    if (!originalHref) {
      originalHref = orDie(link.getAttribute('href'));
      link.setAttribute('data-original', originalHref);
    }

    link.setAttribute(
      'href',
      hasPending ? '/faviconPending.svg' : originalHref
    );

    return () => {
      link.setAttribute('href', originalHref);
    };
  }, [hasPending]);
}
